/**
 * Gestión → Clientes → **Créditos y vales del cliente**
 * (`/gestion/clientes/saldos`).
 *
 * Puerto de `Cliente::credito`, `Cliente::vales` y `Cliente::pagos` con las
 * vistas `menu/cliente/{credito,vales,pagos}.php`: los **saldos a favor** del
 * cliente.
 *
 * - **Créditos**: la línea que el cliente deja por producto (cantidad máxima y
 *   restante, precio acordado, importe y su restante) con los galones que ya
 *   retiró, lo que lleva pagado y su saldo. Se configura (o acumula) la línea,
 *   se registran retiros, se corrigen, se pagan y se cierra.
 * - **Vales**: el galonaje adelantado a un precio acordado con lo consumido y su
 *   importe pendiente; se liquida por todo el restante.
 * - **Pagos**: los pagos de créditos y vales con su usuario.
 *
 * Reglas conservadas: configurar la línea es **acumulativo**; el pago del crédito
 * es libre y el del vale es por todo el restante; cerrar deja `estado = -1`.
 *
 * ⚠️ **Candado anotado**: el **comprobante** del retiro lo emite el servicio de
 * facturación (el original montaba la venta con su factura y llamaba a
 * `envio_directo` desde `agregar_client_credit`); aquí el retiro se registra con
 * `estado_factura = 0` y queda pendiente de comprobante.
 */

import { Ban, Fuel, Plus, RefreshCw, Wallet } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, cn } from '@/lib/utils';

import { fechaCorta, monto } from '../gestion-formato';
import {
  useAjustarConsumo,
  useCatalogosSaldos,
  useConfigurarCredito,
  useConfigurarVale,
  useConsumosDeCredito,
  useConsumosDeVale,
  useCreditos,
  useFinalizarCredito,
  useFinalizarVale,
  usePagarCredito,
  usePagarVale,
  usePagosSaldos,
  useRegistrarConsumo,
  useRegistrarConsumoVale,
  useVales,
} from '../clientes-saldos.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const PESTANAS = [
  { clave: 'creditos', etiqueta: 'Créditos' },
  { clave: 'vales', etiqueta: 'Vales' },
  { clave: 'pagos', etiqueta: 'Pagos' },
] as const;

type ClavePestana = (typeof PESTANAS)[number]['clave'];

/** Etiqueta del estado de una línea (-1 cerrada, 0 con saldo, 1 activa). */
function tonoLinea(estado: number): 'success' | 'warning' | 'neutral' {
  if (estado === 1) return 'success';
  if (estado === 0) return 'warning';

  return 'neutral';
}

function etiquetaLinea(estado: number): string {
  if (estado === 1) return 'ACTIVA';
  if (estado === 0) return 'CON SALDO';
  if (estado === -1) return 'CERRADA';

  return String(estado);
}

export function ClientesSaldosPage() {
  useDocumentTitle('Gestión · Créditos y vales de clientes');

  const [pestana, setPestana] = useState<ClavePestana>('creditos');
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState('1');
  const [saldo, setSaldo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [consumos, setConsumos] = useState<{ tipo: 'credito' | 'vale'; id: number } | null>(null);
  const [pago, setPago] = useState<{ tipo: 'credito' | 'vale'; id: number; nombre: string } | null>(null);
  const [retiro, setRetiro] = useState<{ tipo: 'credito' | 'vale'; id: number; nombre: string } | null>(null);
  const [porCerrar, setPorCerrar] = useState<
    { tipo: 'credito' | 'vale'; id: number; nombre: string } | null
  >(null);
  const [nueva, setNueva] = useState<'credito' | 'vale' | null>(null);

  const filtros = useMemo(
    () => ({ search: texto.trim(), estado, saldo, cliente_id: undefined }),
    [texto, estado, saldo],
  );

  const creditos = useCreditos(filtros);
  const vales = useVales(filtros);
  const pagos = usePagosSaldos(filtros);

  const consulta = pestana === 'creditos' ? creditos : pestana === 'vales' ? vales : pagos;

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const listaCreditos = creditos.data?.creditos ?? [];
  const listaVales = vales.data?.vales ?? [];

  const totalCreditos =
    listaCreditos.reduce((suma, fila) => suma + Number(fila.total_credito_restante), 0);
  const totalVales = listaVales.reduce((suma, fila) => suma + Number(fila.importe_restante), 0);

  const cerrar = useFinalizarCredito();
  const cerrarVale = useFinalizarVale();

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Créditos y vales de clientes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Saldos a favor del cliente: el crédito o el vale que dejó por producto, lo que ya
            retiró y lo que le queda por pagar.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          leftIcon={<RefreshCw className={cn('size-4', consulta.isFetching && 'animate-spin')} />}
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            icono: Wallet,
            etiqueta: 'Créditos activos',
            valor: String(listaCreditos.length),
            detalle: `Por cobrar ${formatCurrency(totalCreditos)}`,
            tono: 'bg-brand-50 text-brand-600',
          },
          {
            icono: Fuel,
            etiqueta: 'Vales activos',
            valor: String(listaVales.length),
            detalle: `Por cobrar ${formatCurrency(totalVales)}`,
            tono: 'bg-amber-50 text-amber-600',
          },
          {
            icono: Wallet,
            etiqueta: 'Pagos de créditos',
            valor: formatCurrency(pagos.data?.total_creditos ?? 0),
            detalle: `${pagos.data?.creditos.length ?? 0} pagos`,
            tono: 'bg-emerald-50 text-emerald-600',
          },
          {
            icono: Wallet,
            etiqueta: 'Pagos de vales',
            valor: formatCurrency(pagos.data?.total_vales ?? 0),
            detalle: `${pagos.data?.vales.length ?? 0} liquidaciones`,
            tono: 'bg-ink-100 text-ink-600',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="flex items-start gap-3 p-4">
              <span className={cn('rounded-full p-2', tarjeta.tono)}>
                <tarjeta.icono className="size-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="line-clamp-2 text-xs text-ink-500">{tarjeta.detalle}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-end gap-3">
            {PESTANAS.map((opcion) => (
              <Button
                key={opcion.clave}
                variant={pestana === opcion.clave ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setPestana(opcion.clave)}
              >
                {opcion.etiqueta}
              </Button>
            ))}

            <Input
              label="Buscar"
              containerClassName="min-w-[240px] flex-1"
              placeholder="Cliente, documento o producto…"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
            />

            <Button
              size="sm"
              leftIcon={<Plus className="size-4" />}
              disabled={pestana === 'pagos'}
              onClick={() => setNueva(pestana === 'vales' ? 'vale' : 'credito')}
            >
              {pestana === 'vales' ? 'Nuevo vale' : 'Nuevo crédito'}
            </Button>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado</span>
              <Select className={CONTROL} value={estado} onChange={(evento) => setEstado(evento.target.value)}>
                <option value="1">Activas</option>
                <option value="0">Con saldo</option>
                <option value="todos">Todas</option>
              </Select>
            </label>

            {pestana === 'creditos' ? (
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Saldo</span>
                <Select className={CONTROL} value={saldo} onChange={(evento) => setSaldo(evento.target.value)}>
                  <option value="">Todos</option>
                  <option value="con">Con saldo</option>
                  <option value="sin">Sin saldo</option>
                </Select>
              </label>
            ) : null}
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {pestana === 'creditos' && listaCreditos.length > 0 ? (
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cliente</th>
                    <th className="px-2 py-2">Producto</th>
                    <th className="px-2 py-2 text-right">Cantidad (máx / rest.)</th>
                    <th className="px-2 py-2 text-right">Precio acordado</th>
                    <th className="px-2 py-2 text-right">Importe (total / por cobrar)</th>
                    <th className="px-2 py-2 text-right">Retirado</th>
                    <th className="px-2 py-2 text-right">Pagado</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {listaCreditos.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="max-w-[16rem] px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cliente ?? ''}>
                          {fila.cliente ?? `Cliente ${fila.cliente_id}`}
                        </p>
                        <p className="text-xs text-ink-500">{fila.cliente_doc ?? fila.ruc ?? '—'}</p>
                      </td>
                      <td className="px-2 py-2">
                        <p className="font-semibold text-ink-800">{fila.producto ?? '—'}</p>
                        <p className="text-xs text-ink-500">{fila.producto_tipo ?? ''}</p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">{fila.cantidad_maxima.toFixed(2)}</p>
                        <p className="text-xs text-ink-500">restan {fila.cantidad_restante.toFixed(2)}</p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {monto(fila.precio_acordado)}
                        {fila.acordado ? (
                          <span className="block text-xs text-ink-500">acordado</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">{monto(fila.total_credito)}</p>
                        <p className="text-xs text-ink-500">por cobrar {monto(fila.total_credito_restante)}</p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {fila.galones_retirados.toFixed(2)}
                        <p className="text-xs text-ink-500">{monto(fila.importe_retirado)}</p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {monto(fila.pagado)}
                        <p className="text-xs text-ink-500">{fila.pagos} pago(s)</p>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Badge variant={tonoLinea(fila.estado)}>{etiquetaLinea(fila.estado)}</Badge>
                        <p className="text-xs text-ink-500">
                          {Number(fila.estado_pago) === 1 ? 'pagado' : 'pendiente de pago'}
                        </p>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConsumos({ tipo: 'credito', id: fila.id })}
                          >
                            Retiros
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={Number(fila.estado) !== 1}
                            onClick={() =>
                              setRetiro({
                                tipo: 'credito',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            Retirar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={Number(fila.total_credito_restante) <= 0}
                            onClick={() =>
                              setPago({
                                tipo: 'credito',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            Pagar
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Cerrar la línea"
                            aria-label={`Cerrar el crédito de ${fila.cliente ?? fila.id}`}
                            disabled={Number(fila.estado) === -1}
                            onClick={() =>
                              setPorCerrar({
                                tipo: 'credito',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            <Ban className="size-4 text-rose-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {pestana === 'vales' && listaVales.length > 0 ? (
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cliente</th>
                    <th className="px-2 py-2">Producto</th>
                    <th className="px-2 py-2 text-right">Galonaje (acordado / restante)</th>
                    <th className="px-2 py-2 text-right">Precio</th>
                    <th className="px-2 py-2 text-right">Consumido</th>
                    <th className="px-2 py-2 text-right">Importe restante</th>
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {listaVales.map((fila) => (
                    <tr key={fila.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="max-w-[16rem] px-2 py-2">
                        <p className="truncate font-semibold text-ink-900" title={fila.cliente ?? ''}>
                          {fila.cliente ?? `Cliente ${fila.cliente_id}`}
                        </p>
                        <p className="text-xs text-ink-500">{fila.cliente_doc ?? fila.ruc ?? '—'}</p>
                      </td>
                      <td className="px-2 py-2 font-semibold text-ink-800">{fila.producto ?? '—'}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <p className="font-semibold">{fila.galonaje_acordado.toFixed(2)}</p>
                        <p className="text-xs text-ink-500">restan {fila.galonaje_restante.toFixed(2)}</p>
                      </td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">{monto(fila.precio)}</td>
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        {fila.galonaje_consumido.toFixed(2)}
                        <p className="text-xs text-ink-500">
                          {fila.consumos} consumo(s)
                          {fila.ultimo_vale ? ` · ${fila.ultimo_vale}` : ''}
                        </p>
                      </td>
                      <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                        {monto(fila.importe_restante)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Badge variant={tonoLinea(fila.estado)}>{etiquetaLinea(fila.estado)}</Badge>
                        <p className="text-xs text-ink-500">
                          {Number(fila.estado_pago) === 1 ? 'liquidado' : 'pendiente de pago'}
                        </p>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConsumos({ tipo: 'vale', id: fila.id })}
                          >
                            Consumos
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={Number(fila.estado) !== 1}
                            onClick={() =>
                              setRetiro({
                                tipo: 'vale',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            Consumir
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={Number(fila.importe_restante) <= 0}
                            onClick={() =>
                              setPago({
                                tipo: 'vale',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            Liquidar
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Cerrar la línea"
                            aria-label={`Cerrar el vale de ${fila.cliente ?? fila.id}`}
                            disabled={Number(fila.estado) === -1}
                            onClick={() =>
                              setPorCerrar({
                                tipo: 'vale',
                                id: fila.id,
                                nombre: `${fila.cliente ?? ''} · ${fila.producto ?? ''}`,
                              })
                            }
                          >
                            <Ban className="size-4 text-rose-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {pestana === 'pagos' ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink-800">
                  Pagos de créditos ({(pagos.data?.creditos ?? []).length})
                </h3>
                <ul className="max-h-[50vh] space-y-2 overflow-auto">
                  {(pagos.data?.creditos ?? []).map((fila) => (
                    <li key={fila.id} className="rounded-card border border-ink-100 p-3">
                      <p className="font-semibold text-ink-900">
                        {monto(Number(fila.total_pago))} · {fila.cliente ?? '—'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {fila.producto ?? ''} · {fila.fecha_pago ? fechaCorta(fila.fecha_pago) : ''} ·{' '}
                        {fila.usuario ?? 'sin usuario'}
                      </p>
                    </li>
                  ))}
                </ul>
                {(pagos.data?.creditos ?? []).length === 0 ? (
                  <p className="rounded-card border border-dashed border-ink-200 p-4 text-center text-sm text-ink-500">
                    Sin pagos de créditos.
                  </p>
                ) : null}
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink-800">
                  Liquidaciones de vales ({(pagos.data?.vales ?? []).length})
                </h3>
                <ul className="max-h-[50vh] space-y-2 overflow-auto">
                  {(pagos.data?.vales ?? []).map((fila) => (
                    <li key={fila.id} className="rounded-card border border-ink-100 p-3">
                      <p className="font-semibold text-ink-900">
                        {monto(Number(fila.total_pago))} · {fila.cliente ?? '—'}
                      </p>
                      <p className="text-xs text-ink-500">
                        {fila.producto ?? ''} · {fila.fecha_pago ? fechaCorta(fila.fecha_pago) : ''} ·{' '}
                        {fila.usuario ?? 'sin usuario'}
                      </p>
                    </li>
                  ))}
                </ul>
                {(pagos.data?.vales ?? []).length === 0 ? (
                  <p className="rounded-card border border-dashed border-ink-200 p-4 text-center text-sm text-ink-500">
                    Sin liquidaciones de vales.
                  </p>
                ) : null}
              </section>
            </div>
          ) : null}

          {!consulta.isLoading &&
          ((pestana === 'creditos' && listaCreditos.length === 0) ||
            (pestana === 'vales' && listaVales.length === 0)) ? (
            <p className="rounded-card border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">
              No hay {pestana === 'creditos' ? 'créditos' : 'vales'} con esos filtros. Se configuran
              desde la fila del cliente, en su ficha.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Alert variant="info" title="Cómo funciona">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Wallet className="size-3.5" aria-hidden="true" />
              Configurar la línea es <strong>acumulativo</strong>: lo que se añade se suma a la
              cantidad y al importe que el cliente ya tenía de ese producto.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Fuel className="size-3.5" aria-hidden="true" />
              El <strong>retiro</strong> baja el stock del producto; el <strong>pago</strong> del
              crédito es libre (parcial) y el del vale es por todo el restante.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Wallet className="size-3.5" aria-hidden="true" />
              Cerrar una línea la deja en <strong>-1</strong>; si todavía tiene saldo, el sistema
              avisa y pide confirmarlo.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Ban className="size-3.5" aria-hidden="true" />
              El <strong>comprobante</strong> del retiro lo emite el servicio de facturación: aquí
              queda anotado con su comprobante pendiente.
            </span>
          </li>
        </ul>
      </Alert>

      <NuevaLineaDialog tipo={nueva} onClose={() => setNueva(null)} onAviso={mostrar} />

      <ConsumosDialog
        objetivo={consumos}
        onClose={() => setConsumos(null)}
        onAviso={mostrar}
      />

      <RetiroDialog objetivo={retiro} onClose={() => setRetiro(null)} onAviso={mostrar} />

      <PagoDialog objetivo={pago} onClose={() => setPago(null)} onAviso={mostrar} />

      <ConfirmDialog
        open={porCerrar !== null}
        title={`¿Cerrar la línea de ${porCerrar?.nombre ?? ''}?`}
        description="Una línea cerrada deja de admitir retiros. Si todavía tiene saldo, el sistema lo dirá y habrá que confirmar de nuevo."
        confirmLabel="Cerrar línea"
        tone="danger"
        loading={cerrar.isPending || cerrarVale.isPending}
        onClose={() => setPorCerrar(null)}
        onConfirm={() => {
          const objetivo = porCerrar;

          setPorCerrar(null);

          if (!objetivo) return;

          const manejar = {
            onSuccess: (respuesta: { mensaje: string }) => mostrar(respuesta.mensaje),
            onError: (fallo: unknown) => {
              if (fallo instanceof ApiError && fallo.status === 409) {
                // Tenía saldo: se vuelve a pedir confirmación forzando el cierre.
                setPorCerrar({ ...objetivo });

                return;
              }

              mostrar(fallo instanceof ApiError ? fallo.message : 'No se pudo cerrar la línea.');
            },
          };

          if (objetivo.tipo === 'credito') cerrar.mutate({ id: objetivo.id }, manejar);
          else cerrarVale.mutate(objetivo.id, manejar);
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Retiros y consumos
 * ------------------------------------------------------------------ */

function ConsumosDialog({
  objetivo,
  onAviso,
  onClose,
}: {
  objetivo: { tipo: 'credito' | 'vale'; id: number } | null;
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}) {
  const esCredito = objetivo?.tipo === 'credito';
  const creditos = useConsumosDeCredito(esCredito ? (objetivo?.id ?? null) : null);
  const vales = useConsumosDeVale(esCredito ? null : (objetivo?.id ?? null));
  const ajustar = useAjustarConsumo();

  const consulta = esCredito ? creditos : vales;
  const [edicion, setEdicion] = useState<{ id: number; galones: number } | null>(null);

  return (
    <Dialog
      open={objetivo !== null}
      onClose={onClose}
      size="lg"
      title={esCredito ? 'Retiros del crédito' : 'Consumos del vale'}
      description="Los galones que el cliente ya retiró de esta línea."
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

      <ul className="max-h-[50vh] space-y-2 overflow-auto">
        {(esCredito ? (creditos.data?.consumos ?? []) : []).map((fila) => (
          <li
            key={fila.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-3"
          >
            <div>
              <p className="font-semibold text-ink-900">
                {Number(fila.cantidad_galones).toFixed(2)} gal · {monto(Number(fila.total))}
              </p>
              <p className="text-xs text-ink-500">
                {fila.created_at ? fechaCorta(fila.created_at) : ''}
                {fila.placa ? ` · placa ${fila.placa}` : ''} · {fila.usuario ?? 'sin usuario'} ·{' '}
                {Number(fila.estado_factura) === 1 ? 'con comprobante' : 'comprobante pendiente'}
              </p>
            </div>

            <div className="flex items-end gap-2">
              <Input
                label="Galones"
                type="number"
                step="0.01"
                containerClassName="w-28"
                defaultValue={Number(fila.cantidad_galones)}
                onChange={(evento) => setEdicion({ id: fila.id, galones: Number(evento.target.value) })}
              />
              <Button
                variant="outline"
                size="sm"
                loading={ajustar.isPending}
                onClick={() => {
                  if (edicion && edicion.id === fila.id) {
                    ajustar.mutate(
                      { consumoId: edicion.id, galones: edicion.galones },
                      {
                        onSuccess: (respuesta) => onAviso(respuesta.mensaje),
                        onError: (fallo) =>
                          onAviso(fallo instanceof ApiError ? fallo.message : 'No se pudo corregir.'),
                      },
                    );
                  }
                }}
              >
                Corregir
              </Button>
            </div>
          </li>
        ))}

        {!esCredito
          ? (vales.data?.consumos ?? []).map((fila) => (
              <li
                key={fila.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-3"
              >
                <div>
                  <p className="font-semibold text-ink-900">
                    {Number(fila.galonaje_vendido).toFixed(2)} gal
                  </p>
                  <p className="text-xs text-ink-500">
                    {fila.created_at ? fechaCorta(fila.created_at) : ''}
                    {fila.numero_vale ? ` · vale ${fila.numero_vale}` : ''} · {fila.usuario ?? 'sin usuario'}
                  </p>
                </div>
              </li>
            ))
          : null}
      </ul>
    </Dialog>
  );
}

function RetiroDialog({
  objetivo,
  onAviso,
  onClose,
}: {
  objetivo: { tipo: 'credito' | 'vale'; id: number; nombre: string } | null;
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}) {
  const consumo = useRegistrarConsumo();
  const consumoVale = useRegistrarConsumoVale();

  const [datos, setDatos] = useState({ galones: '', precio: '', placa: '', numero_vale: '' });
  const [error, setError] = useState<string | null>(null);

  const esCredito = objetivo?.tipo === 'credito';

  const enviar = () => {
    setError(null);

    if (!objetivo) return;

    const manejar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        onAviso(respuesta.mensaje);
        onClose();
      },
      onError: (fallo: unknown) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el consumo.'),
    };

    if (esCredito) {
      consumo.mutate(
        {
          id: objetivo.id,
          galones: Number(datos.galones),
          precio: datos.precio ? Number(datos.precio) : undefined,
          placa: datos.placa || undefined,
        },
        manejar,
      );
    } else {
      consumoVale.mutate(
        { id: objetivo.id, galones: Number(datos.galones), numero_vale: datos.numero_vale || undefined },
        manejar,
      );
    }
  };

  return (
    <Dialog
      open={objetivo !== null}
      onClose={onClose}
      size="md"
      title={esCredito ? 'Retirar galones del crédito' : 'Consumir galones del vale'}
      description={objetivo?.nombre}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={consumo.isPending || consumoVale.isPending}
            disabled={!datos.galones}
            onClick={enviar}
          >
            Registrar
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo registrar">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Galones"
          type="number"
          step="0.01"
          value={datos.galones}
          onChange={(evento) => setDatos((previo) => ({ ...previo, galones: evento.target.value }))}
        />
        {esCredito ? (
          <>
            <Input
              label="Precio (vacío = precio acordado)"
              type="number"
              step="0.01"
              value={datos.precio}
              onChange={(evento) => setDatos((previo) => ({ ...previo, precio: evento.target.value }))}
            />
            <Input
              label="Placa"
              value={datos.placa}
              onChange={(evento) => setDatos((previo) => ({ ...previo, placa: evento.target.value.toUpperCase() }))}
            />
          </>
        ) : (
          <Input
            label="Número de vale"
            value={datos.numero_vale}
            onChange={(evento) => setDatos((previo) => ({ ...previo, numero_vale: evento.target.value }))}
          />
        )}
      </div>

      <Alert variant="info" title="Qué pasa al registrar">
        <span className={ICONO_EN_ALERTA}>
          Baja el saldo de la línea y el <strong>stock</strong> del producto. El comprobante del retiro
          lo emite el servicio de facturación, así que queda pendiente.
        </span>
      </Alert>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Pagos
 * ------------------------------------------------------------------ */

function PagoDialog({
  objetivo,
  onAviso,
  onClose,
}: {
  objetivo: { tipo: 'credito' | 'vale'; id: number; nombre: string } | null;
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}) {
  const credito = usePagarCredito();
  const vale = usePagarVale();

  const [monto, setMonto] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Clave de un solo envío del pago en curso: si el envío se repite (timeout), el API
  // devuelve el pago ya registrado en lugar de cobrar dos veces.
  const claveDeEnvio = useRef<string | null>(null);

  const esCredito = objetivo?.tipo === 'credito';

  const enviar = () => {
    setError(null);

    if (!objetivo) return;

    const manejar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        claveDeEnvio.current = null;
        onAviso(respuesta.mensaje);
        onClose();
      },
      onError: (fallo: unknown) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el pago.'),
    };

    if (esCredito) {
      const clave = (claveDeEnvio.current ??= nuevaClaveDeEnvio());

      credito.mutate({ id: objetivo.id, monto: Number(monto), claveIdempotencia: clave }, manejar);
    } else {
      vale.mutate(objetivo.id, manejar);
    }
  };

  return (
    <Dialog
      open={objetivo !== null}
      onClose={onClose}
      size="sm"
      title={esCredito ? 'Pagar el crédito' : 'Liquidar el vale'}
      description={objetivo?.nombre}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={credito.isPending || vale.isPending}
            disabled={esCredito && !monto}
            onClick={enviar}
          >
            {esCredito ? 'Registrar pago' : 'Liquidar'}
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo registrar">
          {error}
        </Alert>
      ) : null}

      {esCredito ? (
        <Input
          label="Importe a pagar (S/)"
          type="number"
          step="0.01"
          value={monto}
          onChange={(evento) => setMonto(evento.target.value)}
        />
      ) : (
        <Alert variant="info" title="Liquidación del vale">
          <span className={ICONO_EN_ALERTA}>
            El vale se liquida por todo su restante (<strong>galonaje restante × precio</strong>), que
            es lo que hacía el panel anterior.
          </span>
        </Alert>
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Nueva línea (crédito o vale)
 * ------------------------------------------------------------------ */

/**
 * Configura la línea del cliente (`Cliente::agregar_credito` / `agregar_vale`).
 *
 * Es **acumulativo**: si el cliente ya tiene línea de ese producto, lo que se
 * indica aquí se **suma** a la cantidad y al importe que ya tenía. En el crédito,
 * dejar la cantidad en blanco (o en 0) significa **sin tope**.
 */
function NuevaLineaDialog({
  tipo,
  onAviso,
  onClose,
}: {
  tipo: 'credito' | 'vale' | null;
  onAviso: (mensaje: string) => void;
  onClose: () => void;
}) {
  const catalogos = useCatalogosSaldos(tipo !== null);
  const credito = useConfigurarCredito();
  const vale = useConfigurarVale();

  const [datos, setDatos] = useState({
    cliente_id: 0,
    producto_id: 0,
    cantidad: '',
    precio: '',
    total: '',
  });
  const [error, setError] = useState<string | null>(null);

  const esCredito = tipo === 'credito';

  const enviar = () => {
    setError(null);

    const manejar = {
      onSuccess: (respuesta: { mensaje: string }) => {
        onAviso(respuesta.mensaje);
        setDatos({ cliente_id: 0, producto_id: 0, cantidad: '', precio: '', total: '' });
        onClose();
      },
      onError: (fallo: unknown) =>
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo configurar la línea.'),
    };

    if (esCredito) {
      credito.mutate(
        {
          cliente_id: datos.cliente_id,
          producto_id: datos.producto_id,
          cantidad_maxima: Number(datos.cantidad) || 0,
          precio_acordado: Number(datos.precio) || 0,
          total_credito: Number(datos.total) || 0,
        },
        manejar,
      );
    } else {
      vale.mutate(
        {
          cliente_id: datos.cliente_id,
          producto_id: datos.producto_id,
          galonaje_acordado: Number(datos.cantidad) || 0,
          precio: Number(datos.precio) || 0,
        },
        manejar,
      );
    }
  };

  return (
    <Dialog
      open={tipo !== null}
      onClose={onClose}
      size="lg"
      title={esCredito ? 'Nuevo crédito del cliente' : 'Nuevo vale del cliente'}
      description={
        esCredito
          ? 'El cliente deja un importe por un producto y va retirando galones. Sin cantidad, el crédito queda sin tope.'
          : 'El cliente adelanta un galonaje a un precio acordado.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={credito.isPending || vale.isPending}
            disabled={!datos.cliente_id || !datos.producto_id}
            onClick={enviar}
          >
            Configurar
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="error" title="No se pudo configurar">
          {error}
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Cliente</span>
          <Select
            className={CONTROL}
            value={datos.cliente_id}
            onChange={(evento) => setDatos((previo) => ({ ...previo, cliente_id: Number(evento.target.value) }))}
          >
            <option value={0}>SELECCIONE</option>
            {(catalogos.data?.clientes ?? []).map((cliente) => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.nombre} · {cliente.numero_doc}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-ink-700">Producto</span>
          <Select
            className={CONTROL}
            value={datos.producto_id}
            onChange={(evento) => setDatos((previo) => ({ ...previo, producto_id: Number(evento.target.value) }))}
          >
            <option value={0}>SELECCIONE</option>
            {(catalogos.data?.productos ?? []).map((producto) => (
              <option key={producto.id} value={producto.id}>
                {producto.nombre}
              </option>
            ))}
          </Select>
        </label>

        <Input
          label={esCredito ? 'Cantidad máxima (vacío = sin tope)' : 'Galonaje acordado'}
          type="number"
          step="0.01"
          value={datos.cantidad}
          onChange={(evento) => setDatos((previo) => ({ ...previo, cantidad: evento.target.value }))}
        />
        <Input
          label="Precio acordado (S/)"
          type="number"
          step="0.01"
          value={datos.precio}
          onChange={(evento) => setDatos((previo) => ({ ...previo, precio: evento.target.value }))}
        />
        {esCredito ? (
          <Input
            label="Importe del crédito (S/)"
            type="number"
            step="0.01"
            value={datos.total}
            onChange={(evento) => setDatos((previo) => ({ ...previo, total: evento.target.value }))}
          />
        ) : null}
      </div>

      <Alert variant="info" title="Es acumulativo">
        <span className={ICONO_EN_ALERTA}>
          Si el cliente ya tiene {esCredito ? 'crédito' : 'vale'} de ese producto, lo que indiques se
          <strong> suma</strong> a lo que ya tenía, como en el panel anterior.
        </span>
      </Alert>
    </Dialog>
  );
}
